import React, { useRef, useEffect, useState } from "react";
import { Card, Spinner, Row, Col, Container } from "react-bootstrap";
import Chart from "chart.js/auto";

interface ClientesPedidos {
  NOME_FANTASIA: string;
  TOTAL_PECAS: string;
  TOTAL_PEDIDOS: number;
  TOTAL_VENDIDO: string;
  cnpj: string;
}

interface Props {
  dadosClientes: { vendas_x_cliente: ClientesPedidos[] } | null;
  loading: boolean;
  error: string | null;
}

const ClientesGaugeChart: React.FC<Props> = ({ dadosClientes, loading, error }) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const [chartInstance, setChartInstance] = useState<Chart | null>(null);

  // Garantindo que os dados existem antes de acessar
  const clientes = dadosClientes?.vendas_x_cliente ?? [];

  // Contamos o total de clientes
  const totalClientes = clientes.length;

  // Filtramos apenas os clientes com pedidos > 0
  const clientesComPedido = clientes.filter(cliente => cliente.TOTAL_PEDIDOS > 0).length;

  // Calculamos a porcentagem de clientes com pedidos
  const percentualClientesComPedido = totalClientes > 0 ? (clientesComPedido / totalClientes) * 100 : 0;

  useEffect(() => {
    if (clientes.length > 0 && chartRef.current) {
      const ctx = chartRef.current.getContext("2d");
      if (!ctx) return;

      if (chartInstance) {
        chartInstance.destroy();
      }

      const newChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          datasets: [
            {
              data: [percentualClientesComPedido, 100 - percentualClientesComPedido],
              backgroundColor: ["#28a745", "#e0e0e0"], // Verde = com pedido, Cinza = sem pedido
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "70%", // Formato de velocímetro
          circumference: 180, // Mostra metade do gráfico
          rotation: 270, // Inicia de baixo para cima
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
          animation: {
            animateRotate: true,
            animateScale: true,
          },
        },
      });

      setChartInstance(newChart);
    }
  }, [dadosClientes]);

  return (
    <Card className="p-4 shadow" style={{minHeight: "500px"}}>
      <h5>Nº Total Clientes || Nº Clientes com Pedido</h5>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p>Carregando dados...</p>
        </div>
      ) : error ? (
        <div className="text-center text-danger">
          <p>{error}</p>
        </div>
      ) : (
        <div className="d-flex flex-column align-items-center" style={{ position: "relative", width: "100%", height: "250px" }}>
          <canvas ref={chartRef}></canvas>
          <div
            style={{
              position: "absolute",
              top: "55%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "clamp(14px, 5vw, 20px)", // Ajusta dinamicamente a fonte para telas pequenas
              fontWeight: "bold",
              textAlign: "center",
              lineHeight: "1.2", // Para garantir que o texto não ultrapasse
            }}
          >
            {percentualClientesComPedido.toFixed(0)}% Clientes com Pedido
          </div>
        </div>
      )}
      <div className="mt-3 text-center">
        <p><strong>Total de Clientes:</strong> {totalClientes}</p>
        <p><strong>Clientes com Pedido:</strong> {clientesComPedido}</p>
      </div>
    </Card>
  );
};

export default ClientesGaugeChart;
